import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../../../software/api";
import { toast } from "sonner";
import { Clock, ChevronLeft, ChevronRight, Save, Download, CalendarDays, Users } from "lucide-react";
import { exportXLSX } from "../reports/exportUtils";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const TASK_TYPES = ["Workshop", "On-site", "Deliveries", "Sales", "Service Call", "Admin", "Other"];

const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmtNice = (d) => d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });

function calcHours(start, finish, breakMins) {
  if (!start || !finish) return null;
  const [sh, sm] = start.split(":").map(Number);
  const [fh, fm] = finish.split(":").map(Number);
  if ([sh, sm, fh, fm].some((x) => Number.isNaN(x))) return null;
  let mins = fh * 60 + fm - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;
  mins -= Number(breakMins || 0);
  return Math.max(0, Math.round((mins / 60) * 100) / 100);
}

const blankRow = () => ({ start_time: "", finish_time: "", break_mins: "", hours: "", task_type: "", notes: "" });

export default function TimeSheets({ user, isAdmin }) {
  const [employees, setEmployees] = useState([]);
  const [employee, setEmployee] = useState("");
  const [weekStartDay, setWeekStartDay] = useState("Thursday");
  const [weekOffset, setWeekOffset] = useState(0);
  const [rows, setRows] = useState({}); // keyed by ISO date
  const [saving, setSaving] = useState(false);
  const [allStaff, setAllStaff] = useState(false);
  const [allRows, setAllRows] = useState([]);

  useEffect(() => {
    api("GET", "/timesheet/employees").then((n) => {
      const own = user?.role === "technician";
      setEmployees(own ? [user.name] : n);
      // default to a name matching the logged-in user, but NEVER overwrite an active pick
      const mine = n.find((x) => x.toLowerCase() === (user?.name || "").toLowerCase());
      setEmployee((prev) => (own ? user.name : prev || mine || ""));
    }).catch(() => {});
    api("GET", "/timesheet/settings").then((s) => setWeekStartDay(s.week_start_day || "Thursday")).catch(() => {});
  }, [user]);

  // ---- week window (7 days starting on weekStartDay) ----
  const week = useMemo(() => {
    const startIdx = DAYS.indexOf(weekStartDay);
    const base = new Date();
    base.setHours(0, 0, 0, 0);
    base.setDate(base.getDate() + weekOffset * 7);
    const back = (base.getDay() - startIdx + 7) % 7;
    const startDate = new Date(base);
    startDate.setDate(base.getDate() - back);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);
      return d;
    });
  }, [weekStartDay, weekOffset]);

  const startISO = toISO(week[0]);
  const endISO = toISO(week[6]);

  const loadWeek = useCallback(() => {
    if (!employee) { setRows({}); return; }
    api("GET", `/timesheets?employee=${encodeURIComponent(employee)}&start=${startISO}&end=${endISO}`)
      .then((data) => {
        const map = {};
        (data || []).forEach((r) => {
          map[r.work_date] = {
            start_time: r.start_time || "", finish_time: r.finish_time || "",
            break_mins: r.break_mins || "", hours: r.hours == null ? "" : r.hours,
            task_type: r.task_type || "", notes: r.notes || "",
          };
        });
        setRows(map);
      }).catch(() => {});
  }, [employee, startISO, endISO]);

  useEffect(() => { loadWeek(); }, [loadWeek]);

  useEffect(() => {
    if (!isAdmin || !allStaff) return;
    api("GET", `/timesheets?start=${startISO}&end=${endISO}`).then(setAllRows).catch(() => {});
  }, [isAdmin, allStaff, startISO, endISO]);

  const setField = (iso, field, val) => {
    setRows((prev) => {
      const row = { ...(prev[iso] || blankRow()), [field]: val };
      if (["start_time", "finish_time", "break_mins"].includes(field)) {
        const h = calcHours(row.start_time, row.finish_time, row.break_mins);
        if (h != null) row.hours = h;
      }
      return { ...prev, [iso]: row };
    });
  };

  const weekTotal = useMemo(
    () => week.reduce((sum, d) => sum + (Number(rows[toISO(d)]?.hours) || 0), 0),
    [week, rows]
  );

  const saveWeek = async () => {
    if (!employee) { toast.error("Please choose your name first"); return; }
    setSaving(true);
    try {
      const entries = week.map((d) => ({ work_date: toISO(d), ...(rows[toISO(d)] || blankRow()) }));
      const res = await api("POST", "/timesheets/save", { employee, entries });
      toast.success(`Time sheet saved (${res.saved} day${res.saved === 1 ? "" : "s"})`);
      loadWeek();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const saveSetting = async (day) => {
    setWeekStartDay(day);
    try { await api("PUT", "/timesheet/settings", { week_start_day: day }); toast.success(`Week now starts on ${day}`); }
    catch (e) { toast.error(e.message); }
  };

  const doExport = () => {
    const cols = [
      { key: "employee", label: "Employee" }, { key: "day", label: "Day" }, { key: "work_date", label: "Date" },
      { key: "start_time", label: "Start" }, { key: "finish_time", label: "Finish" },
      { key: "break_mins", label: "Break (min)" }, { key: "hours", label: "Hours", money: false },
      { key: "task_type", label: "Task" }, { key: "notes", label: "Notes" },
    ];
    let data;
    if (isAdmin && allStaff) {
      data = allRows.map((r) => ({ ...r, day: DAYS[new Date(r.work_date).getDay()] }));
    } else {
      data = week.map((d) => ({ employee, day: DAYS[d.getDay()], work_date: toISO(d), ...(rows[toISO(d)] || blankRow()) }))
        .filter((r) => r.hours || r.start_time || r.notes || r.task_type);
    }
    if (!data.length) { toast.error("Nothing to export for this week"); return; }
    exportXLSX(`TimeSheet_${(isAdmin && allStaff) ? "AllStaff" : employee.replace(/\s+/g, "")}_${startISO}`, cols, data);
    toast.success("Exported — open in Excel and transpose into Xero");
  };

  const allTotal = useMemo(() => allRows.reduce((s, r) => s + (Number(r.hours) || 0), 0), [allRows]);

  return (
    <div className="h-[calc(100vh-72px)] overflow-hidden flex flex-col bg-slate-50" data-testid="timesheets-screen">
      {/* Header band */}
      <div className="shrink-0 bg-gradient-to-r from-[#123a63] to-[#2f5fae] text-white px-5 py-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 mr-2">
          <Clock className="h-7 w-7" />
          <div>
            <div className="font-display text-2xl font-extrabold leading-none">TIME SHEETS</div>
            <div className="text-[11px] text-cyan-100 uppercase tracking-wider">Daily & weekly hours · export to Xero</div>
          </div>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <Users className="h-4 w-4 text-cyan-100" />
          <select
            data-testid="ts-employee-select"
            value={employee}
            onChange={(e) => setEmployee(e.target.value)}
            disabled={user?.role === "technician"}
            className="rounded-lg px-3 py-1.5 text-sm font-bold text-slate-800 bg-white min-w-[190px]"
          >
            <option value="">— Choose your name —</option>
            {employees.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>

        {/* Week nav */}
        <div className="flex items-center gap-1 bg-white/15 rounded-lg px-1 py-0.5">
          <button data-testid="ts-week-prev" onClick={() => setWeekOffset((w) => w - 1)} className="p-1.5 rounded hover:bg-white/20"><ChevronLeft className="h-5 w-5" /></button>
          <button data-testid="ts-week-today" onClick={() => setWeekOffset(0)} className="px-2 text-xs font-bold hover:bg-white/20 rounded flex items-center gap-1">
            <CalendarDays className="h-4 w-4" /> {fmtNice(week[0])} – {fmtNice(week[6])}
          </button>
          <button data-testid="ts-week-next" onClick={() => setWeekOffset((w) => w + 1)} className="p-1.5 rounded hover:bg-white/20"><ChevronRight className="h-5 w-5" /></button>
        </div>

        <div data-testid="ts-week-total" className="bg-emerald-500 text-white rounded-lg px-3 py-1.5 font-extrabold text-sm shadow">
          Week total: {weekTotal.toFixed(2)} h
        </div>

        <button data-testid="ts-save-week" onClick={saveWeek} disabled={saving || !employee}
          className="bg-white text-[#123a63] rounded-lg px-4 py-1.5 font-extrabold text-sm shadow hover:brightness-95 disabled:opacity-50 flex items-center gap-1.5">
          <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save Week"}
        </button>
        <button data-testid="ts-export" onClick={doExport}
          className="bg-amber-400 text-slate-900 rounded-lg px-3 py-1.5 font-extrabold text-sm shadow hover:brightness-95 flex items-center gap-1.5">
          <Download className="h-4 w-4" /> Export
        </button>
      </div>

      {/* Admin controls row */}
      {isAdmin && (
        <div className="shrink-0 bg-slate-100 border-b border-slate-200 px-5 py-2 flex items-center gap-4 text-sm">
          <label className="flex items-center gap-2 font-semibold text-slate-700">
            <input type="checkbox" data-testid="ts-allstaff-toggle" checked={allStaff} onChange={(e) => setAllStaff(e.target.checked)} />
            View all staff (this week)
          </label>
          <label className="flex items-center gap-2 text-slate-700 ml-auto">
            Payroll week starts on:
            <select data-testid="ts-weekstart-select" value={weekStartDay} onChange={(e) => saveSetting(e.target.value)}
              className="rounded border border-slate-300 px-2 py-1 text-sm font-bold">
              {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        {isAdmin && allStaff ? (
          <div className="bg-white rounded-xl shadow border border-slate-200 overflow-hidden max-w-5xl mx-auto">
            <div className="bg-[#123a63] text-white px-4 py-2 font-bold flex items-center justify-between">
              <span>All Staff — {fmtNice(week[0])} to {fmtNice(week[6])}</span>
              <span data-testid="ts-allstaff-total">Total: {allTotal.toFixed(2)} h</span>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-100 text-slate-600 text-left">
                <th className="px-3 py-2">Employee</th><th className="px-3 py-2">Date</th><th className="px-3 py-2">Day</th>
                <th className="px-3 py-2">Start</th><th className="px-3 py-2">Finish</th><th className="px-3 py-2 text-right">Hours</th>
                <th className="px-3 py-2">Task</th><th className="px-3 py-2">Notes</th>
              </tr></thead>
              <tbody data-testid="ts-allstaff-table">
                {allRows.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400">No entries this week.</td></tr>}
                {allRows.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-3 py-1.5 font-semibold">{r.employee}</td>
                    <td className="px-3 py-1.5">{r.work_date}</td>
                    <td className="px-3 py-1.5">{DAYS[new Date(r.work_date).getDay()]}</td>
                    <td className="px-3 py-1.5">{r.start_time}</td>
                    <td className="px-3 py-1.5">{r.finish_time}</td>
                    <td className="px-3 py-1.5 text-right font-bold">{r.hours ?? ""}</td>
                    <td className="px-3 py-1.5">{r.task_type}</td>
                    <td className="px-3 py-1.5 text-slate-500">{r.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : !employee ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center text-slate-400">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-40" />
              <p className="font-semibold text-slate-500">Choose your name above to start your time sheet.</p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow border border-slate-200 overflow-hidden max-w-5xl mx-auto" data-testid="ts-week-grid">
            <table className="w-full text-sm">
              <thead><tr className="bg-[#123a63] text-white text-left">
                <th className="px-3 py-2.5 w-40">Day</th>
                <th className="px-3 py-2.5">Start</th>
                <th className="px-3 py-2.5">Finish</th>
                <th className="px-3 py-2.5">Break (min)</th>
                <th className="px-3 py-2.5">Hours</th>
                <th className="px-3 py-2.5">Task type</th>
                <th className="px-3 py-2.5">Notes</th>
              </tr></thead>
              <tbody>
                {week.map((d) => {
                  const iso = toISO(d);
                  const row = rows[iso] || blankRow();
                  const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                  return (
                    <tr key={iso} data-testid={`ts-row-${iso}`} className={`border-t border-slate-100 ${isWeekend ? "bg-slate-50/70" : ""}`}>
                      <td className="px-3 py-1.5">
                        <div className="font-bold text-slate-800">{DAYS[d.getDay()]}</div>
                        <div className="text-[11px] text-slate-400">{fmtNice(d)}</div>
                      </td>
                      <td className="px-2 py-1.5"><input data-testid={`ts-start-${iso}`} type="time" value={row.start_time} onChange={(e) => setField(iso, "start_time", e.target.value)} className="rounded border border-slate-200 px-2 py-1 w-28" /></td>
                      <td className="px-2 py-1.5"><input data-testid={`ts-finish-${iso}`} type="time" value={row.finish_time} onChange={(e) => setField(iso, "finish_time", e.target.value)} className="rounded border border-slate-200 px-2 py-1 w-28" /></td>
                      <td className="px-2 py-1.5"><input data-testid={`ts-break-${iso}`} type="number" min="0" step="5" value={row.break_mins} onChange={(e) => setField(iso, "break_mins", e.target.value)} className="rounded border border-slate-200 px-2 py-1 w-20" placeholder="0" /></td>
                      <td className="px-2 py-1.5"><input data-testid={`ts-hours-${iso}`} type="number" min="0" step="0.25" value={row.hours} onChange={(e) => setField(iso, "hours", e.target.value)} className="rounded border border-slate-300 px-2 py-1 w-20 font-bold text-[#123a63]" placeholder="0" /></td>
                      <td className="px-2 py-1.5">
                        <select data-testid={`ts-task-${iso}`} value={row.task_type} onChange={(e) => setField(iso, "task_type", e.target.value)} className="rounded border border-slate-200 px-2 py-1">
                          <option value="">—</option>
                          {TASK_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-1.5"><input data-testid={`ts-notes-${iso}`} value={row.notes} onChange={(e) => setField(iso, "notes", e.target.value)} className="rounded border border-slate-200 px-2 py-1 w-full min-w-[160px]" placeholder="Job / description" /></td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-slate-100 border-t-2 border-[#123a63]">
                  <td className="px-3 py-2 font-extrabold text-[#123a63]" colSpan={4}>Weekly total</td>
                  <td className="px-2 py-2 font-extrabold text-[#123a63]">{weekTotal.toFixed(2)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
            <div className="px-4 py-2 text-[11px] text-slate-400 bg-slate-50 border-t border-slate-100">
              Tip: enter Start &amp; Finish (and any unpaid break) and Hours fill in automatically — or just type the Hours directly (e.g. 8).
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
